import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Message,
  sendMessage,
  fetchChatThreads,
  fetchChatThreadDetails,
} from "./actions";
import { ChatSession } from "../../../interfaces/chat";

interface QAState {
  currentChatMessages: Message[]; // Messages of the active session
  chatSessions: ChatSession[];
  activeChatId: string | null;
  isLoading: boolean;
  error: string | null;
  threads: any[]; // Add threads array to store fetched threads
}

// Default initial state - no localStorage
const getInitialState = (): QAState => {
  return {
    currentChatMessages: [],
    chatSessions: [],
    activeChatId: null,
    isLoading: false,
    error: null,
    threads: [],
  };
};

const qaSlice = createSlice({
  name: "qa",
  initialState: getInitialState(),
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      const activeSession = state.chatSessions.find(
        (session) => session.id === state.activeChatId,
      );
      if (activeSession) {
        activeSession.messages.push(action.payload);
        state.currentChatMessages = [...activeSession.messages]; // Create new array reference
      }
    },
    updateMessage: (
      state,
      action: PayloadAction<{ id: string; content: string }>,
    ) => {
      const activeSession = state.chatSessions.find(
        (session) => session.id === state.activeChatId,
      );
      if (activeSession) {
        const message = activeSession.messages.find(
          (msg) => msg.id === action.payload.id,
        );
        if (message) {
          message.content = action.payload.content;
          state.currentChatMessages = [...activeSession.messages]; // Create new array reference
        }
      }
    },
    setMessageStreaming: (
      state,
      action: PayloadAction<{ id: string; isStreaming: boolean }>,
    ) => {
      const activeSession = state.chatSessions.find(
        (session) => session.id === state.activeChatId,
      );
      if (activeSession) {
        const message = activeSession.messages.find(
          (msg) => msg.id === action.payload.id,
        );
        if (message) {
          message.isStreaming = action.payload.isStreaming;
          state.currentChatMessages = [...activeSession.messages]; // Create new array reference
        }
      }
    },
    startNewChatSession: (state, action?: PayloadAction<string | any>) => {
      const newSessionId = action?.payload || `new_${Date.now()}`;
      const newSession: ChatSession = {
        id: newSessionId,
        messages: [],
        createdAt: new Date(),
        title: "New Chat",
      };

      // Add new session to the beginning of the list
      state.chatSessions.unshift(newSession);
      state.activeChatId = newSessionId;
      state.currentChatMessages = [];
    },
    loadChatSession: (state, action: PayloadAction<string>) => {
      const sessionToLoad = state.chatSessions.find(
        (session) => session.id === action.payload,
      );
      if (sessionToLoad) {
        state.activeChatId = sessionToLoad.id;
        state.currentChatMessages = [...sessionToLoad.messages]; // Create new array reference
      } else {
        // If session doesn't exist locally, just set the active ID and clear messages
        state.activeChatId = action.payload;
        state.currentChatMessages = [];
      }
    },
    deleteChatSession: (state, action: PayloadAction<string>) => {
      state.chatSessions = state.chatSessions.filter(
        (session) => session.id !== action.payload,
      );
      if (state.activeChatId === action.payload) {
        // If the deleted session was active, activate the first one or start a new one
        if (state.chatSessions.length > 0) {
          state.activeChatId = state.chatSessions[0].id;
          state.currentChatMessages = [...state.chatSessions[0].messages];
        } else {
          const newSessionId = `new_${Date.now()}`;
          const newSession: ChatSession = {
            id: newSessionId,
            messages: [],
            createdAt: new Date(),
            title: "New Chat",
          };
          state.chatSessions.push(newSession);
          state.activeChatId = newSessionId;
          state.currentChatMessages = [];
        }
      }
    },
    updateSessionTitle: (
      state,
      action: PayloadAction<{ id: string; title: string }>,
    ) => {
      const session = state.chatSessions.find(
        (s) => s.id === action.payload.id,
      );
      if (session) {
        session.title = action.payload.title;
      }
    },
    updateSessionWithThreadInfo: (
      state,
      action: PayloadAction<{ oldId: string; threadId: string; title: string }>,
    ) => {
      const sessionIndex = state.chatSessions.findIndex(
        (s) => s.id === action.payload.oldId,
      );
      if (sessionIndex >= 0) {
        const session = state.chatSessions[sessionIndex];

        // Update the session with the new thread ID and title
        session.id = action.payload.threadId;
        session.title = action.payload.title;

        // Update the active chat ID if this was the active session
        if (state.activeChatId === action.payload.oldId) {
          state.activeChatId = action.payload.threadId;
        }

        // Force a re-render by updating the currentChatMessages reference
        if (state.activeChatId === action.payload.threadId) {
          state.currentChatMessages = [...session.messages];
        }

        // Move the updated session to the front of the list since it's the most recent
        const updatedSession = state.chatSessions.splice(sessionIndex, 1)[0];
        state.chatSessions.unshift(updatedSession);
      }
    },
    addThreadToList: (
      state,
      action: PayloadAction<{ id: number; title: string; created_at: string }>,
    ) => {
      // Add the new thread to the beginning of the threads list
      const newThread = {
        id: action.payload.id,
        title: action.payload.title,
        created_at: action.payload.created_at,
        updated_at: action.payload.created_at,
      };

      // Check if thread already exists to avoid duplicates
      const existingThreadIndex = state.threads.findIndex(
        (thread) => thread.id === action.payload.id,
      );
      if (existingThreadIndex === -1) {
        // Add to the beginning of the array
        state.threads = [newThread, ...state.threads];
      } else {
        // Update existing thread and move to beginning
        const existingThread = state.threads[existingThreadIndex];
        state.threads.splice(existingThreadIndex, 1);
        state.threads.unshift({ ...existingThread, ...newThread });
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // New cases for chat threads
      .addCase(fetchChatThreads.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchChatThreads.fulfilled, (state, action) => {
        // Sort threads by creation date (newest first)
        const sortedThreads = action.payload.sort(
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        state.threads = sortedThreads;
      })
      .addCase(fetchChatThreads.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // New cases for thread details
      .addCase(fetchChatThreadDetails.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchChatThreadDetails.fulfilled, (state, action) => {
        // Convert the thread details into a chat session format
        const threadDetails = action.payload;
        const newSession: ChatSession = {
          id: threadDetails.id.toString(),
          messages: threadDetails.messages.reverse().map((msg: any) => ({
            id: msg.id.toString(),
            content: msg.content,
            isUser: msg.role === "user",
            timestamp: new Date(msg.created_at),
          })),
          createdAt: new Date(threadDetails.created_at),
          title: threadDetails.title,
        };

        // Update or add the session
        const existingSessionIndex = state.chatSessions.findIndex(
          (s) => s.id === newSession.id,
        );
        if (existingSessionIndex >= 0) {
          state.chatSessions[existingSessionIndex] = newSession;
        } else {
          state.chatSessions.unshift(newSession);
        }

        // Set as active session and update current messages
        state.activeChatId = newSession.id;
        state.currentChatMessages = [...newSession.messages]; // Create new array reference
      })
      .addCase(fetchChatThreadDetails.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  addMessage,
  updateMessage,
  setMessageStreaming,
  startNewChatSession,
  loadChatSession,
  deleteChatSession,
  updateSessionTitle,
  updateSessionWithThreadInfo,
  addThreadToList,
  setError,
} = qaSlice.actions;

export default qaSlice.reducer;
